import { Children, isValidElement, useMemo } from 'react';

import { BaseDynamicGrid } from './_components/BaseDynamicGrid';
import { CardCustomizeOverlay } from './_components/CardCustomizeOverlay';
import type { DynamicGridProps } from './types';
import { useDynamicGridContext } from './useDynamicGridContext';

/**
 * Context-connected variant of DynamicGrid. Derives visible children from
 * layout (single source of truth) via a key→element map — no separate
 * hiddenItems filter needed.
 */
export const DynamicGrid = ({ children, ...props }: DynamicGridProps) => {
  const {
    layout,
    onLayoutChange,
    isCustomizing,
    toggleItemVisibility,
    rowHeight,
  } = useDynamicGridContext();

  const childMap = useMemo(() => {
    const map = new Map<string, React.ReactElement>();
    Children.forEach(children, (child) => {
      if (isValidElement(child) && child.key != null) {
        map.set(String(child.key), child);
      }
    });
    return map;
  }, [children]);

  // Filter layout to items that have a matching child — keeps layout and
  // children in sync even if DASHBOARD_ITEMS and JSX children diverge.
  const visibleLayout = useMemo(
    () => layout.filter((item) => childMap.has(item.i)),
    [layout, childMap]
  );

  const visibleChildren = useMemo(
    () =>
      visibleLayout.map((item) => {
        const child = childMap.get(item.i)!;
        return (
          <CardCustomizeOverlay
            key={child.key}
            itemId={item.i}
            onHide={toggleItemVisibility}
          >
            {child}
          </CardCustomizeOverlay>
        );
      }),
    [visibleLayout, childMap, toggleItemVisibility]
  );

  return (
    <BaseDynamicGrid
      {...props}
      layout={visibleLayout}
      onLayoutChange={onLayoutChange}
      isCustomizing={isCustomizing}
      rowHeight={rowHeight}
    >
      {visibleChildren}
    </BaseDynamicGrid>
  );
};
